<template>

    <div id="show-order">

        <div v-if="loaded">
            <div class="flex items-center mb-4">
                <h1 class="flex-1 text-2xl font-medium text-gray-900 capitalize mb-2">
                    Order <span class="text-indigo-500 font-medium">{{ form.reference }}</span>
                </h1>

                <button @click.prevent="goBack" class="btn-outline">Go Back</button>

                {{ form.invoice }}

                <a :href="`/app/download/invoice/${order.invoice}`" class="btn ml-2" v-if="order.invoice">
                    Download Invoice
                </a>
            </div>

            <div class="app-panel p-3 mb-4">

                <ul class="grid grid-cols-3 text-center border-t-1 border-gray-300">
                    <li class="px-6">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 mx-auto text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h3 class="flex items-center justify-center text-gray-700 my-2 pl-5" :class="{ 'font-medium': orderStatusIs(['open', 'processing', 'complete']) }">
                            Created
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2 text-brand-500" viewBox="0 0 20 20" fill="currentColor" v-if="orderStatusIs(['open', 'processing', 'complete'])">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                            </svg>
                        </h3>
                        <p class="text-xs text-gray-500">
                            Your order has been created and is waiting to be processed.
                        </p>
                    </li>
                    <li class="px-6">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 mx-auto text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <h3 class="flex items-center justify-center text-gray-700 my-2 pl-5" :class="{ 'font-medium': orderStatusIs(['processing', 'complete']) }">
                            Processing
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2 text-brand-500" viewBox="0 0 20 20" fill="currentColor" v-if="orderStatusIs(['processing', 'complete'])">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                            </svg>
                        </h3>
                        <p class="text-xs text-gray-500">
                            Your order is being processed, the length of time this takes is determined by the order quantity.
                        </p>
                    </li>
                    <li class="px-6">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 mx-auto text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                        </svg>
                        <h3 class="flex items-center justify-center text-gray-700 my-2" :class="{ 'pl-5 font-medium': orderStatusIs('complete') }">
                            Complete
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2 text-brand-500" viewBox="0 0 20 20" fill="currentColor" v-if="orderStatusIs('complete')">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                            </svg>
                        </h3>
                        <p class="text-xs text-gray-500">
                            Your order has completed, you may down download your codes.
                        </p>
                    </li>
                </ul>
                <div v-if="orderStatusIs('complete')">
                    <hr class="my-2 border-gray-200">
                    <div class="text-right">
                        <ConfirmationModal v-if="isAdmin && orderStatusIs('complete') && isCancellable" button_classes="btn-error text-xs my-4 ml-2" class="inline" button_text="Cancel Order" @confirmModal="cancelOrder(order.reference)">
                            <div class="mt-3 text-center sm:mt-5">
                                <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                    Are you sure you want to cancel this order?
                                </h3>
                                <div class="mt-2">
                                    <p class="text-sm text-gray-500" v-if="order.payment_type !== 'unlimited'">
                                        {{ formatCurrency(order.currency, order.payment_amount / 100) }} will be refunded.
                                    </p>
                                </div>
                            </div>
                        </ConfirmationModal>
                    </div>
                </div>
                <div v-if="orderStatusIs('failed')">
                    <hr class="my-2 border-gray-200">
                    <div class="text-right">
                        <ConfirmationModal v-if="isAdmin && orderStatusIs('failed')" button_classes="btn-error text-xs my-4 ml-2" class="inline" button_text="Reprocess Order" @confirmModal="reprocessOrder(order.reference)">
                            <div class="mt-3 text-center sm:mt-5">
                                <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                    Are you sure you want to reprocess this order?
                                </h3>
                            </div>
                        </ConfirmationModal>
                    </div>
                </div>
            </div>

            <div class="lg:flex">

                <div class="app-panel w-full mb-6 lg:w-1/2 lg:mr-3">

                    <table class="w-full h-48">
                        <thead>
                        <tr>
                            <th class="text-left font-medium text-gray-600" colspan="2">Details</th>
                        </tr>
                        </thead>
                        <tbody class="text-gray-600">
                            <tr>
                                <td class="text-left text-sm">Quantity</td>
                                <td class="text-right font-medium">{{ order.total_quantity }}</td>
                            </tr>
                            <tr>
                                <td class="text-left text-sm">Status</td>
                                <td class="text-right">
                                    <div v-if="isEditingStatus">
                                        <form @submit.prevent="updateOrderStatus()" class="text-right">
                                            <select name="update_status" class="form-control-input" v-model="updatedStatus"
                                                    style="width: auto !important; display: inline-block !important;">
                                                <option v-for="(status, key) in orderStatuses" :key="key" v-bind:value="status" v-text="ucfirst(status)"></option>
                                            </select>

                                            <button class="btn-error text-xs ml-2">Update Status</button>
                                        </form>

                                        <div class="flex mt-6 justify-end">
                                            <div class="flex border-l-4 border-error-500 bg-error-50 text-gray-700 px-4 py-1 rounded tracking-wide shadow items-center text-error-400" v-if="updateStatusError">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                                <p class="text-error-500 text-sm" v-text="updateStatusError"></p>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-else>
                                        <span :class="`status-box ${statusBox(order.status)}`" v-text="order.status"></span>

                                        <svg v-if="isAdmin" v-on:click="isEditingStatus = true" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 ml-2 inline overflow-visible cursor-pointer text-gray-500"
                                             fill="none" viewBox="0 0 32 32" stroke="currentColor" focusable="false"
                                             role="img" aria-label="Change Order Status" title="Change Order Status">
                                            <path d="M27 0c2.761 0 5 2.239 5 5 0 1.126-0.372 2.164-1 3l-2 2-7-7 2-2c0.836-0.628 1.874-1 3-1zM2 23l-2 9 9-2 18.5-18.5-7-7-18.5 18.5zM22.362 11.362l-14 14-1.724-1.724 14-14 1.724 1.724z"></path>
                                        </svg>
                                    </div>
                                </td>
                            </tr>
                            <template v-if="order.status === 'failed' && form.order_exception && Array.isArray(form.order_exception) && form.order_exception.length > 0">
                                <tr v-for="(exception, index) in form.order_exception" :key="index">
                                    <td v-if="index===0" class="text-left text-sm">Response from processor</td>
                                    <td v-else class="text-left text-sm"></td>
                                    <td class="text-right text-sm">{{ exception.exception_message }}</td>
                                </tr>
                            </template>
                            <tr>
                                <td class="text-left text-sm">User</td>
                                <td class="text-right text-sm">
                                    {{ order.user_name }} &lt;<a :href="`mailto:${order.user_email}`" target="_blank">{{ order.user_email }}</a>&gt;
                                </td>
                            </tr>
                            <tr>
                                <td class="text-left text-sm">Order Date</td>
                                <td class="text-right text-sm">{{ formatDate(form.order.created_at) }}</td>
                            </tr>
                            <tr>
                                <td class="text-left text-sm">Payment Method</td>
                                <td class="text-right text-sm">
                                    <span class="status-box">{{ order.payment_type }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <hr class="my-2 border-gray-400">

                    <table class="w-full">
                        <tbody>
                        <tr>
                            <td class="text-left text-sm font-medium text-gray-700">Payment</td>
                            <td class="text-right text-xl font-medium text-gray-900">{{ formatCurrency(order.currency, order.payment_amount / 100) }}</td>
                        </tr>
                        </tbody>
                    </table>

                </div>

                <div class="app-panel w-full mb-6 lg:w-1/2 lg:ml-3">
                    <table class="w-full h-48">
                        <thead>
                            <tr>
                                <th class="text-left font-medium text-gray-600" colspan="2">Totals</th>
                            </tr>
                        </thead>
                        <tbody class="text-gray-600">
                            <tr>
                                <td class="text-left text-sm">Gross Total</td>
                                <td class="text-right">{{ formatCurrency(order.currency, order.gross_total / 100) }}</td>
                            </tr>
                            <tr>
                                <td class="text-left text-sm">Tax</td>
                                <td class="text-right">{{ formatCurrency(order.currency, order.order_tax / 100) }}</td>
                            </tr>
                            <tr>
                                <td class="text-left text-sm">Discount</td>
                                <td class="text-right">{{ formatCurrency(order.currency, order.discount / 100) }}</td>
                            </tr>
                            <tr>
                                <td class="text-left text-sm">Transaction Fees</td>
                                <td class="text-right">{{ formatCurrency(order.currency, order.transaction_fee / 100) }}</td>
                            </tr>
                            <tr>
                                <td class="text-left text-sm">Transaction Fee Tax</td>
                                <td class="text-right">{{ formatCurrency(order.currency, order.transaction_fee_tax / 100) }}</td>
                            </tr>
                        </tbody>
                    </table>

                    <hr class="my-2 border-gray-400">

                    <table class="w-full">
                        <tbody>
                        <tr>
                            <td class="text-left text-sm font-medium text-gray-700">Total</td>
                            <td class="text-right text-xl font-medium text-gray-900">{{ formatCurrency(order.currency, order.net_total / 100) }}</td>
                        </tr>
                        </tbody>
                    </table>

                </div>

            </div>

            <div class="flex items-center mb-4">
                <h2 class="flex-1 text-lg font-medium">Order Items</h2>

                <div class="hidden lg:flex items-center" :class="{ 'form-control-error': form.fieldHasErrors('account_id') }" v-show="orderStatusIs('complete')">
                    <label for="account_id" class="w-48 font-medium text-sm text-gray-700 mr-2 text-right">Export Format</label>
                    <select name="account_id" id="account_id" class="form-control-input" v-model="form.export_format" @change="updateDownloadFormat">
                        <option :value="method.value" v-for="method in exportFormats">{{ method.name }}</option>
                    </select>
                </div>

                <a :href="`/app/download/order/${form.reference}?format=${form.export_format}`" class="btn ml-2" v-show="orderStatusIs('complete')">Download Codes</a>

                <div class="hidden lg:flex items-center text-sm" v-show="!orderStatusIs('complete')">
                    <p>
                        Once your order has completed, you can download your codes
                    </p>
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" focusable="false" role="img" aria-label="Change Order Reference">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                </div>

                <button class="btn ml-2" disabled aria-disabled="true" aria-label="Download Codes" v-show="!orderStatusIs('complete')">Download Codes</button>
            </div>

            <ShowOrderItems v-model="form"></ShowOrderItems>

        </div>
        <Loading :message="`Loading Order ${form.reference}`" v-else></Loading>

    </div>

</template>

<script>
import Loading from "../Support/Loading";
import {Order} from "../../app/Order/Order";
import {OrderStatus} from "../../app/OrderStatus/OrderStatus";
import ShowOrderItems from "./ShowOrderItems";
import {FormattingMixin, HttpStatusMixin, NavigationMixin} from "../../support/mixins";
import ConfirmationModal from "../Support/ConfirmationModal";

export default {
    name: "ShowOrder",
    mixins: [NavigationMixin, FormattingMixin, HttpStatusMixin],
    components: {
        ShowOrderItems,
        Loading,
        ConfirmationModal
    },
    data() {
        return {
            form: false,
            loaded: false,
            exportFormats: [
                { value: 'Csv', name: 'Comma Separated File (.csv)'},
                { value: 'Xls', name: 'Excel Spreadsheet (.xls)'},
            ],
            orderStatuses: [],
            updatedStatus: null,
            isEditingStatus: false,
            updateStatusError: null,
        }
    },
    computed: {
        order() {
            return this.form.order;
        },
        fund() {
            return this.form.order.fund;
        },
        items() {
            return [];
        },
        isAdmin() {
            return this.$parent.isAdmin;
        },
        isCancellable() {
            return this.form.order.is_refundable;
        },
    },
    methods: {
        updateOrderStatus() {
            this.updateStatusError = null;

            if (this.updatedStatus === this.form.order.status) {
                this.isEditingStatus = false;
                return;
            }

            this.form.updateOrderStatus(this.form.order.reference, this.updatedStatus)
                .then((result) => {
                    this.isEditingStatus = false;

                    this.form.order.status = result.data.data.status;
                })
                .catch((error) => {
                    this.updateStatusError = error.response.data.message ?? error.message;
                });
        },
        updateDownloadFormat() {
            localStorage.setItem('default_export_format', this.form.export_format);
        },
        orderStatusIs(status) {
            let order_status = this.form.order.status;

            if (Array.isArray(status)) {
                return status.includes(order_status)
            }

            return order_status === status;
        },
        cancelOrder(reference) {
            let vm = this;

            vm.form.cancelOrder(reference).then(result => {
                vm.$forceUpdate();
            });
        },
        reprocessOrder(reference) {
            let vm = this;

            vm.form.reprocessOrder(reference).then(result => {
                vm.$forceUpdate();
            });
        },
    },
    async created() {
        this.orderStatuses = await OrderStatus.listOrderStatuses();
    },
    mounted() {
        let vm = this;

        new Promise(resolve => {
            vm.form = new Order(vm.$parent.auth, [], vm.$route.params.reference);

            // set the default export format for the current browser (based on last selection)
            vm.form.export_format = localStorage.getItem('default_export_format') ?? 'Csv';

            vm.form.showOrder(vm.reference).then(r => {
                this.updatedStatus = this.form.order.status;

                if (vm.form.order.status == 'failed') {
                    vm.form.showOrderException(vm.reference).then(r => {
                        resolve(true);
                    });
                } else {
                    resolve(true);
                }
            });
        }).then(r => {
            vm.loaded = r;
        });
    }
}
</script>
